import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Settings2, Webhook } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ScrollArea from "@/components/ui/shared/ScrollArea";
import BlockList from "@/components/fluxos/BlockList";
import AddBlockMenu from "@/components/fluxos/AddBlockMenu";
import FluxoWhatsAppPreview from "@/components/fluxos/FluxoWhatsAppPreview";
import { BLOCK_META } from "@/components/fluxos/blockMeta";
import FluxoSettingsModal from "@/components/fluxos/FluxoSettingsModal";
import {
  createEmptyBlock,
  generateId,
  generateWebhookToken,
  useFluxos,
  type FluxoBlock,
  type FluxoBlockType,
  type FluxoSettings,
} from "@/hooks/useFluxos";
import { useAssistants } from "@/hooks/useAssistants";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

const collisionDetection: CollisionDetection = (args) => {
  const pointerHits = pointerWithin(args);
  return pointerHits.length > 0 ? pointerHits : rectIntersection(args);
};

const FluxoEditor: React.FC = () => {
  const { t } = useTranslation("automations");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getFluxo, createFluxo, updateFluxo, loading: fluxosLoading } = useFluxos();
  const { assistants } = useAssistants();

  const isEditing = Boolean(id);
  const draftId = useMemo(() => id || generateId(), [id]);
  const [name, setName] = useState("");
  const [blocks, setBlocks] = useState<FluxoBlock[]>([]);
  const [loaded, setLoaded] = useState(!isEditing);
  const [saving, setSaving] = useState(false);
  const [draggingPaletteType, setDraggingPaletteType] = useState<FluxoBlockType | null>(null);
  const [settings, setSettings] = useState<FluxoSettings>(() => ({
    webhookEnabled: false,
    webhookToken: generateWebhookToken(),
  }));
  const [settingsOpen, setSettingsOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (!id || loaded) return;
    if (fluxosLoading) return;

    const fluxo = getFluxo(id);
    if (fluxo) {
      setName(fluxo.name);
      setBlocks(fluxo.blocks);
      if (fluxo.settings) {
        setSettings(fluxo.settings);
      }
      setLoaded(true);
    } else {
      toast.error(t("flow.notFound"));
      navigate("/fluxos");
    }
  }, [id, loaded, getFluxo, navigate, fluxosLoading, t]);

  const handleAddBlock = (type: FluxoBlockType) => {
    setBlocks((prev) => [...prev, createEmptyBlock(type)]);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const paletteType = event.active.data.current?.blockType as FluxoBlockType | undefined;
    setDraggingPaletteType(paletteType ?? null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggingPaletteType(null);
    if (!over) return;

    const paletteType = active.data.current?.blockType as FluxoBlockType | undefined;

    if (paletteType) {
      const newBlock = createEmptyBlock(paletteType);
      setBlocks((prev) => {
        const overIndex = prev.findIndex((b) => b.id === over.id);
        if (overIndex < 0) return [...prev, newBlock];
        const next = [...prev];
        next.splice(overIndex, 0, newBlock);
        return next;
      });
      return;
    }

    if (active.id !== over.id) {
      setBlocks((prev) => {
        const oldIndex = prev.findIndex((b) => b.id === active.id);
        const newIndex = prev.findIndex((b) => b.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return prev;
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t("flow.nameRequired"));
      return;
    }
    if (blocks.length === 0) {
      toast.error(t("flow.blockRequired"));
      return;
    }

    setSaving(true);
    try {
      if (isEditing && id) {
        const ok = await updateFluxo(id, { name: name.trim(), blocks, settings });
        if (!ok) return;
        toast.success(t("flow.updated"));
      } else {
        const created = await createFluxo({
          id: draftId,
          name: name.trim(),
          blocks,
          settings,
        });
        if (!created) return;
        toast.success(t("flow.created"));
      }
      navigate("/fluxos");
    } finally {
      setSaving(false);
    }
  };

  const assistantOptions = assistants.map((a) => ({ id: a.id, name: a.name }));

  if (isEditing && !loaded) {
    return (
      <div className="w-full flex h-[calc(100dvh-120px)] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-3 border-border border-t-primary rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">{t("flow.loadingFlow")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex h-[calc(100dvh-120px)] flex-col">
      <div className="mb-2 pb-4 border-b border-border">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/fluxos")}
            className="w-[34px] h-[34px] flex justify-center items-center border border-border bg-white hover:bg-[#F1F5F9] rounded-[10px] transition-colors dark:border-[#3F3F46] dark:bg-transparent dark:hover:bg-[#27272A]"
            aria-label={t("flow.backToFlows")}
          >
            <ArrowLeft size={18} className="text-[#64748B] dark:text-neutral-400" />
          </button>
          <h2 className="text-[26px] sm:text-[30px] font-bold text-[#0F172A] dark:text-neutral-50">
            {isEditing ? t("flow.editFlow") : t("flow.newFlow")}
          </h2>
          {settings.webhookEnabled && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-green-500/30 bg-green-500/10 text-green-600 text-xs font-medium dark:text-green-400">
              <Webhook size={13} />
              {t("flow.webhookActive")}
            </span>
          )}
        </div>
      </div>

      <div className="w-full mt-3 sm:mt-4 flex flex-wrap gap-3 justify-between items-center">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("flow.namePlaceholder")}
          className="w-[220px] sm:w-[300px] md:w-[400px] min-h-[40px]"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSettingsOpen(true)}
            className="w-[40px] h-[40px] flex justify-center items-center border border-border bg-white hover:bg-[#F1F5F9] rounded-[10px] transition-colors dark:border-[#3F3F46] dark:bg-transparent dark:hover:bg-[#27272A]"
            aria-label={t("flow.advancedSettingsAria")}
            title={t("flow.advancedSettings")}
          >
            <Settings2 size={18} className="text-[#64748B] dark:text-neutral-400" />
          </button>
          <Button
            onClick={() => void handleSave()}
            disabled={saving}
            className="min-h-[40px] min-w-[120px] bg-[#2563EB] hover:bg-[#1D4ED8] text-white shadow-sm dark:bg-[#E4E4E7] dark:text-neutral-900 dark:hover:bg-[#D4D4D8] dark:shadow-none"
          >
            {saving ? t("flow.savingFlow") : t("flow.saveFlow")}
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 mt-3">
        <ScrollArea
          classNames={{
            root: "w-full h-full border border-border rounded-[20px] bg-white shadow-sm dark:border-[#3F3F46] dark:rounded-[12px] dark:bg-[#27272A] dark:shadow-none",
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setDraggingPaletteType(null)}
          >
            <div className="p-4 sm:p-6 grid grid-cols-1 xl:grid-cols-[220px_1fr_360px] gap-6">
              <div className="self-start xl:sticky xl:top-6">
                <p className="text-xs font-medium text-[#64748B] uppercase tracking-wider mb-3 dark:text-neutral-400">
                  {t("flow.addBlock")}
                </p>
                <AddBlockMenu onAdd={handleAddBlock} />
              </div>

              <div className="flex flex-col gap-4 min-w-0">
                <BlockList
                  blocks={blocks}
                  assistants={assistantOptions}
                  accountId={user?.account_id}
                  fluxoId={draftId}
                  onBlocksChange={setBlocks}
                />
              </div>

              <div className="hidden xl:flex flex-col items-center gap-3 self-start sticky top-6">
                <p className="text-xs font-medium text-[#64748B] uppercase tracking-wider dark:text-neutral-400">
                  {t("common.preview")}
                </p>
                <FluxoWhatsAppPreview
                  blocks={blocks}
                  assistants={assistantOptions}
                  fluxoName={name}
                />
              </div>
            </div>

            <DragOverlay>
              {draggingPaletteType ? (
                <div className="flex items-center gap-2 px-3 min-h-[38px] rounded-[10px] border border-border bg-white text-[#0F172A] text-sm shadow-xl w-fit dark:rounded-[8px] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-neutral-50">
                  <Plus size={14} className="text-[#94A3B8] dark:text-neutral-400" />
                  {BLOCK_META[draggingPaletteType].icon}
                  {t(BLOCK_META[draggingPaletteType].labelKey)}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </ScrollArea>
      </div>

      <FluxoSettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={settings}
        onSettingsChange={setSettings}
      />
    </div>
  );
};

export default FluxoEditor;
